import {useState, useEffect} from 'react'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import debounce from 'lodash/debounce'

const DEBOUNCE_WAIT = 750

/**
 * Hook to handle updating an existing cart item via QuickView.
 * Replaces the "Add to Bag" logic with "Update" logic for cart editing.
 */
const useQuickViewCartEdit = ({
    product,
    variant,
    quantity,
    selectedItem,
    setIsQuickViewOpen,
    hasUnselectedVariations,
    setShowOptionsMessage,
    onMissingVariations,
    onUpdateSuccess,
    intl
}) => {
    const [updateError, setUpdateError] = useState('')
    const [isUpdating, setIsUpdating] = useState(false)
    const {data: basket} = useCurrentBasket()

    const updateItemInBasketMutation = useShopperBasketsMutation('updateItemInBasket')
    const removeItemFromBasketMutation = useShopperBasketsMutation('removeItemFromBasket')

    useEffect(() => {
        setUpdateError('')
    }, [variant?.productId])

    const changeItemQuantity = debounce(async (newQuantity, item) => {
        await updateItemInBasketMutation.mutateAsync({
            parameters: {basketId: basket?.basketId, itemId: item.itemId},
            body: {
                productId: item.id || item.productId,
                quantity: parseInt(newQuantity)
            }
        })
    }, DEBOUNCE_WAIT)

    const handleUpdateCartItem = async () => {
        if (hasUnselectedVariations) {
            setShowOptionsMessage(true)
            if (onMissingVariations) {
                onMissingVariations()
            }
            return
        }

        if (!selectedItem || !variant) return

        setIsUpdating(true)

        try {
            const productIds = basket.productItems.map(({productId}) => productId)
            const newVariantId = variant.productId
            const currentItemId = selectedItem.id || selectedItem.productId
            // Preserve the original cart item quantity
            const itemQuantity = selectedItem.quantity || 1

            // The user selected a different variant that is NOT already in the basket
            if (currentItemId !== newVariantId && !productIds.includes(newVariantId)) {
                await updateItemInBasketMutation.mutateAsync({
                    parameters: {
                        basketId: basket.basketId,
                        itemId: selectedItem.itemId
                    },
                    body: {
                        productId: newVariantId,
                        quantity: itemQuantity
                    }
                })
            }
            // The user selected a different variant that already exists in the basket
            else if (currentItemId !== newVariantId && productIds.includes(newVariantId)) {
                await removeItemFromBasketMutation.mutateAsync({
                    parameters: {
                        basketId: basket.basketId,
                        itemId: selectedItem.itemId
                    }
                })
                const existingBasketItem = basket.productItems.find(
                    ({productId}) => productId === newVariantId
                )
                const newQuantity = itemQuantity + existingBasketItem.quantity
                await changeItemQuantity(newQuantity, existingBasketItem)
            }
            // The user only changed the quantity of the same variant
            else if (selectedItem.quantity !== itemQuantity) {
                await changeItemQuantity(itemQuantity, selectedItem)
            }

            setUpdateError('')
            setIsQuickViewOpen(false)
            onUpdateSuccess?.()
        } catch (error) {
            console.error('Error updating cart item:', error)
            let errorMessage
            if (error?.response) {
                try {
                    const errorJson = await error.response.json()
                    errorMessage = errorJson?.detail
                } catch {
                    // response parsing failed, fall through to generic message
                }
            }
            setUpdateError(
                errorMessage ||
                intl.formatMessage({
                    defaultMessage: 'Could not update item. Please try again.',
                    id: 'cart_edit.error.update_failed'
                })
            )
        } finally {
            setIsUpdating(false)
        }
    }

    return {
        handleUpdateCartItem,
        updateError,
        isUpdating
    }
}

export default useQuickViewCartEdit
